import { useState } from 'react';
import { X, Bell, Globe, ExternalLink } from 'lucide-react';
import type { CentralNotification } from '@levelup/central-notifications';
import { useWorkspaceNotifications } from '../hooks/useWorkspaceNotifications';
import { LoadingSpinner } from './ui/LoadingSpinner';

const LEGACY_APP_ID = 'levelup-legacy';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'app' | 'workspace';

/**
 * Notification panel — Central-backed on both tabs.
 *
 * All notifications flow through @levelup/central-notifications. The two tabs
 * partition the same inbox by appId so there is a single source of truth:
 *   - "This App": workspace notifications where appId === 'levelup-legacy'.
 *   - "Workspace": notifications from other apps in the same workspace.
 */
export function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('app');
  const { notifications, isLoading, isError, markRead } = useWorkspaceNotifications({
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const appNotifications = notifications.filter((n) => n.appId === LEGACY_APP_ID);
  const workspaceNotifications = notifications.filter((n) => n.appId !== LEGACY_APP_ID);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Notifications</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <TabButton
          active={activeTab === 'app'}
          onClick={() => setActiveTab('app')}
          icon={<Bell className="h-3.5 w-3.5" />}
          label="This App"
          count={appNotifications.filter((n) => !n.isRead).length}
        />
        <TabButton
          active={activeTab === 'workspace'}
          onClick={() => setActiveTab('workspace')}
          icon={<Globe className="h-3.5 w-3.5" />}
          label="Workspace"
          count={workspaceNotifications.filter((n) => !n.isRead).length}
        />
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        <NotificationList
          notifications={activeTab === 'app' ? appNotifications : workspaceNotifications}
          isLoading={isLoading}
          isError={isError}
          onMarkRead={markRead}
          emptyMessage={
            activeTab === 'app'
              ? 'No active alerts for this app'
              : 'No workspace notifications'
          }
        />
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium ${
        active
          ? 'text-legacy-600 border-b-2 border-legacy-600'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
      {count > 0 && (
        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-legacy-100 text-legacy-700">
          {count}
        </span>
      )}
    </button>
  );
}

function NotificationList({
  notifications,
  isLoading,
  isError,
  onMarkRead,
  emptyMessage,
}: {
  notifications: CentralNotification[];
  isLoading: boolean;
  isError: boolean;
  onMarkRead: (id: string) => void;
  emptyMessage: string;
}) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-sm text-gray-500">
        Unable to load notifications
      </div>
    );
  }

  if (notifications.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">{emptyMessage}</div>;
  }

  return (
    <ul className="divide-y divide-gray-100">
      {notifications.map((n) => (
        <li
          key={n.id}
          className={`px-4 py-3 hover:bg-gray-50 ${n.isRead ? 'opacity-60' : ''}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
              {n.body && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
              )}
              {n.appMetadata?.appDisplayName && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {n.appMetadata.appDisplayName}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {n.actionUrl && (
                <a
                  href={n.actionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              {!n.isRead && (
                <button
                  onClick={() => onMarkRead(n.id)}
                  className="text-xs text-legacy-600 hover:text-legacy-700 whitespace-nowrap"
                >
                  Mark read
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {new Date(n.createdAt).toLocaleDateString()}
          </p>
        </li>
      ))}
    </ul>
  );
}
